import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Paper, 
  Container, 
  Alert,
  CircularProgress 
} from '@mui/material';
import { useSignUp, useClerk } from '@clerk/clerk-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { completeEnterpriseSetup, checkAdminExists } from '../../services/adminService';
import Header from '../common/Header';

const VerifyAdmin: React.FC = () => {
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [canProceed, setCanProceed] = useState(true);
  const { signUp, isLoaded } = useSignUp();
  const clerk = useClerk();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Extract redirectToDashboard from location state
  const redirectToDashboard = location.state?.redirectToDashboard || false;

  // Check if admin already exists on component mount
  useEffect(() => {
    const verifyAdminState = async () => {
      try {
        setLoading(true);
        // Check if an admin user already exists
        const adminExistsResult = await checkAdminExists();
        
        if (adminExistsResult.exists && !localStorage.getItem('enterprise_org_data')) {
          setCanProceed(false);
          // Admin exists and no registration in progress, redirect to login
          navigate('/login');
        }
      } catch (error) {
        console.error('Error checking admin existence:', error);
        // Default to allowing verification if there's an error checking
      } finally {
        setLoading(false);
      }
    };

    verifyAdminState();
  }, [navigate]);

  // Add an effect to check if the user is already verified and signed in
  useEffect(() => {
    if (clerk.user && clerk.session && redirectToDashboard) {
      // If the user is already verified and we should redirect to dashboard
      navigate('/dashboard');
    }
  }, [clerk.user, clerk.session, redirectToDashboard, navigate]);

  const handleVerification = async () => {
    if (!signUp || !verificationCode) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Attempt verification with Clerk
      const result = await signUp.attemptEmailAddressVerification({
        code: verificationCode,
      });

      if (result.status !== 'complete') {
        throw new Error('Verification failed. Please check your code and try again.');
      }

      if (result.createdUserId && result.createdSessionId) {
        // Get organization data from localStorage if available
        const orgDataStr = localStorage.getItem('enterprise_org_data');
        if (!orgDataStr) {
          throw new Error('Registration data not found. Please restart the registration process.');
        }
        
        const organizationData = JSON.parse(orgDataStr);
        
        // Set the session as active
        await clerk.setActive({
          session: result.createdSessionId,
        });

        // Complete the enterprise setup with the created user ID
        const adminSetupResult = await completeEnterpriseSetup(
          { 
            organizationName: organizationData.organizationName,
            // Pass email to ensure it's available if user needs to be created
            email: organizationData.email || '',
            // Add firstName and lastName for the backend user creation
            firstName: organizationData.firstName || '',
            lastName: organizationData.lastName || ''
          },
          result.createdUserId
        );
        
        if (!adminSetupResult.success) {
          throw new Error('Failed to complete admin setup. Please contact support.');
        }

        // Clean up stored data
        localStorage.removeItem('enterprise_org_data');
        
        // Update the user metadata to include the admin role
        await clerk.user?.update({
          unsafeMetadata: {
            role: 'admin',
            isEnterprise: true
          }
        });

        // Redirect to dashboard after successful verification
        navigate('/dashboard');
      } else {
        throw new Error('Account setup incomplete. Please try registering again.');
      }
    } catch (error: any) {
      console.error('Verification error:', error);
      setError(error.message || 'Error verifying your account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isLoaded || loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!canProceed) {
    return (
      <Box
        sx={{
          width: "100%",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          backgroundColor: "#f4f6f8"
        }}
      >
        <Header />
        <Container maxWidth="sm" sx={{ py: 6, flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <Paper elevation={3} sx={{ p: 4, borderRadius: 2, textAlign: 'center' }}>
            <Typography variant="h5" gutterBottom color="error">
              Admin Already Registered
            </Typography>
            <Typography variant="body1" paragraph>
              An administrator account has already been set up for this application.
            </Typography>
            <Box sx={{ mt: 3 }}>
              <Button
                variant="contained"
                onClick={() => navigate('/login')}
                sx={{
                  backgroundColor: "#5D5FEF",
                  '&:hover': { backgroundColor: "#4A4BD5" }
                }}
              >
                Go to Login
              </Button>
            </Box>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#f4f6f8"
      }}
    >
      <Header />

      <Container maxWidth="sm" sx={{ py: 6, flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
          <Typography variant="h4" fontWeight="bold" gutterBottom align="center">
            Verify Your Email
          </Typography>
          
          <Typography variant="body1" paragraph align="center">
            We've sent a verification code to your email.
            Enter the code below to complete your account setup.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Verification Code"
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value)}
            margin="normal"
            required
          />

          <Button
            fullWidth
            variant="contained"
            onClick={handleVerification}
            disabled={loading || !verificationCode}
            sx={{
              mt: 3,
              backgroundColor: "#5D5FEF",
              color: "#fff",
              py: 1.5,
              px: 3,
              '&:hover': {
                backgroundColor: "#4A4BD5",
              }
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Verify & Complete Setup'}
          </Button>
        </Paper>
      </Container>
    </Box>
  );
};

export default VerifyAdmin; 